import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Lock, Send, Star } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  apiSubmitSurveyResponse,
  type SurveyDetail,
  type SurveyAnswerPayload,
} from "@/lib/api";

type AnswerState = Record<
  number,
  { optionId?: number; optionIds?: number[]; textAnswer?: string; ratingValue?: number }
>;

export function SurveyAnswerSheet({
  survey,
  onOpenChange,
}: {
  survey: SurveyDetail | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [answers, setAnswers] = useState<AnswerState>({});
  const [submitting, setSubmitting] = useState(false);

  const toggleOption = (qId: number, optId: number, multi: boolean) => {
    setAnswers((prev) => {
      const cur = prev[qId] || {};
      if (multi) {
        const ids = cur.optionIds || [];
        return { ...prev, [qId]: { ...cur, optionIds: ids.includes(optId) ? ids.filter((x) => x !== optId) : [...ids, optId] } };
      }
      return { ...prev, [qId]: { ...cur, optionId: optId } };
    });
  };

  const handleSubmit = async () => {
    if (!survey) return;
    for (const q of survey.questions) {
      if (!q.isRequired) continue;
      const a = answers[q.id];
      const missing =
        !a ||
        (q.questionType === "single_choice" && !a.optionId) ||
        (q.questionType === "multiple_choice" && (!a.optionIds || a.optionIds.length === 0)) ||
        (q.questionType === "text" && !a.textAnswer?.trim()) ||
        (q.questionType === "rating" && !a.ratingValue);
      if (missing) {
        toast.error(t("surveys.answerRequired", { q: q.questionText }));
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload: SurveyAnswerPayload[] = [];
      for (const q of survey.questions) {
        const a = answers[q.id];
        if (!a) continue;
        if (q.questionType === "single_choice" && a.optionId) payload.push({ questionId: q.id, optionId: a.optionId });
        else if (q.questionType === "multiple_choice" && a.optionIds?.length) payload.push({ questionId: q.id, optionIds: a.optionIds });
        else if (q.questionType === "text" && a.textAnswer) payload.push({ questionId: q.id, textAnswer: a.textAnswer });
        else if (q.questionType === "rating" && a.ratingValue) payload.push({ questionId: q.id, ratingValue: a.ratingValue });
      }
      await apiSubmitSurveyResponse(survey.id, payload);
      toast.success(t("surveys.submitted"));
      setAnswers({});
      qc.invalidateQueries({ queryKey: ["mobile-inbox-surveys"] });
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet
      open={!!survey}
      onOpenChange={(open) => {
        if (!open) setAnswers({});
        onOpenChange(open);
      }}
    >
      <SheetContent side="bottom" className="rounded-t-2xl h-[70vh] flex flex-col p-0 overflow-hidden">
        {survey && (
          <>
            <SheetHeader className="text-left px-4 pt-5 pb-3 border-b border-border/40 shrink-0">
              <SheetTitle className="text-base">{survey.title}</SheetTitle>
              {survey.description && (
                <p className="text-sm text-muted-foreground">{survey.description}</p>
              )}
              {survey.isAnonymous && (
                <div className="flex items-center gap-2 mt-1 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-xs text-emerald-800 dark:text-emerald-400">
                  <Lock className="h-3.5 w-3.5 shrink-0" />
                  {t("surveys.anonymousGuarantee")}
                </div>
              )}
            </SheetHeader>

            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-6">
              {survey.questions.map((q, idx) => {
                const a = answers[q.id] || {};
                return (
                  <div key={q.id} className="space-y-3">
                    <p className="text-sm font-medium leading-snug">
                      {idx + 1}. {q.questionText}
                      {q.isRequired && <span className="text-red-500 ml-1">*</span>}
                    </p>

                    {q.questionType === "single_choice" && (
                      <div className="space-y-2">
                        {q.options.map((o) => (
                          <button
                            key={o.id}
                            className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border text-sm transition-all ${
                              a.optionId === o.id
                                ? "border-violet-300 dark:border-violet-500/40 bg-violet-50 dark:bg-violet-500/10 font-medium text-violet-900 dark:text-violet-200"
                                : "border-border/60 hover:bg-secondary/50"
                            }`}
                            onClick={() => toggleOption(q.id, o.id, false)}
                          >
                            <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 ${a.optionId === o.id ? "border-violet-500" : "border-muted-foreground/30"}`}>
                              {a.optionId === o.id && <div className="h-2 w-2 rounded-full bg-violet-500" />}
                            </div>
                            {o.optionText}
                          </button>
                        ))}
                      </div>
                    )}

                    {q.questionType === "multiple_choice" && (
                      <div className="space-y-2">
                        {q.options.map((o) => {
                          const sel = (a.optionIds || []).includes(o.id);
                          return (
                            <button
                              key={o.id}
                              className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border text-sm transition-all ${
                                sel
                                  ? "border-violet-300 dark:border-violet-500/40 bg-violet-50 dark:bg-violet-500/10 font-medium text-violet-900 dark:text-violet-200"
                                  : "border-border/60 hover:bg-secondary/50"
                              }`}
                              onClick={() => toggleOption(q.id, o.id, true)}
                            >
                              <div className={`h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 ${sel ? "bg-violet-500 border-violet-500 text-white" : "border-muted-foreground/30"}`}>
                                {sel && <CheckCircle2 className="h-3 w-3" />}
                              </div>
                              {o.optionText}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {q.questionType === "text" && (
                      <Textarea
                        placeholder={t("surveys.typeAnswer")}
                        value={a.textAnswer || ""}
                        onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: { ...prev[q.id], textAnswer: e.target.value } }))}
                        className="min-h-[80px]"
                      />
                    )}

                    {q.questionType === "rating" && (
                      <div className="flex gap-1.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            className="p-1 transition-transform hover:scale-110"
                            onClick={() => setAnswers((prev) => ({ ...prev, [q.id]: { ...prev[q.id], ratingValue: n } }))}
                          >
                            <Star className={`h-8 w-8 transition-colors ${n <= (a.ratingValue || 0) ? "text-amber-400 fill-amber-400" : "text-muted-foreground/20"}`} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="border-t px-4 pt-3 shrink-0" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
              <Button onClick={handleSubmit} disabled={submitting} className="w-full gap-2">
                <Send className="h-4 w-4" />
                {submitting ? t("surveys.submitting") : t("surveys.submitSurvey")}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
